import {useMemo} from 'react'
import {Button, Checkbox, Dropdown, Empty, Flex, Space, Table, Tag, Typography,} from 'antd'
import {FilterOutlined} from '@ant-design/icons'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import RecordsCount from '../../common/RecordsCount'
import {useIntl} from 'react-intl'

const DtcMappingValidationTable = ({
  dataSource,
  pagination = false,
  getDtcMappingValidationList = null,
  isSupported = false,
  isMissConfigured = false,
  isMissingComponentInGraph = false,
  onChangeIsSupported = () => {},
  onChangeIsMissConfigured = () => {},
  onChangeIsMissingComponentInGraph = () => {},
}) => {
  const intl = useIntl()

  const columns = useMemo(
    () => [
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'dtcMappingValidation.acronym' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'acronym',
        render: (text) => (
          <EllipsisTooltip className="ellipsis">{text || '-'}</EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'dtcMappingValidation.dtc' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'dtc',
        render: (text) => (
          <EllipsisTooltip className="ellipsis">{text || '-'}</EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'dtcMappingValidation.dtcDescription' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'dtc_description',
        render: (text) => (
          <EllipsisTooltip className="multiline-ellipsis" lineClamp={2}>{text || '-'}</EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'dtcMappingValidation.vin' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'vin',
        render: (text) => (
          <EllipsisTooltip className="ellipsis">{text || '-'}</EllipsisTooltip>
        ),
      },
      {
        title: (
          <EllipsisTooltip className="ellipsis">
            {intl.formatMessage({ id: 'dtcMappingValidation.version' })}
          </EllipsisTooltip>
        ),
        dataIndex: 'data_version',
        render: (text) => <Typography.Text>{text || '-'}</Typography.Text>,
      },
      {
        title: (
          <Flex gap={4} align="center" justify="space-between">
            <EllipsisTooltip className="ellipsis">
              {intl.formatMessage({ id: 'dtcMappingValidation.flags' })}
            </EllipsisTooltip>
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              menu={{
                items: [
                  {
                    key: 'is_supported',
                    label: (
                      <Checkbox
                        checked={isSupported}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          onChangeIsSupported(e.target.checked)
                        }}
                      >
                        {intl.formatMessage({
                          id: 'dtcMappingValidation.moduleInstalled',
                        })}
                      </Checkbox>
                    ),
                  },
                  {
                    key: 'is_misconfigured',
                    label: (
                      <Checkbox
                        checked={isMissConfigured}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          onChangeIsMissConfigured(e.target.checked)
                        }}
                      >
                        {intl.formatMessage({
                          id: 'dtcMappingValidation.misconfigured',
                        })}
                      </Checkbox>
                    ),
                  },
                  {
                    key: 'is_missing_component_in_graph',
                    label: (
                      <Checkbox
                        checked={isMissingComponentInGraph}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          onChangeIsMissingComponentInGraph(e.target.checked)
                        }}
                      >
                        {intl.formatMessage({
                          id: 'dtcMappingValidation.noResultsDisplayed',
                        })}
                      </Checkbox>
                    ),
                  },
                ],
              }}
            >
              <Button
                type="text"
                size="small"
                icon={
                  <FilterOutlined
                    style={{
                      color:
                        isSupported ||
                        isMissConfigured ||
                        isMissingComponentInGraph
                          ? 'var(--color-form-link-normal)'
                          : undefined,
                    }}
                  />
                }
              />
            </Dropdown>
          </Flex>
        ),
        dataIndex: 'flags',
        align: 'right',
        render: (_, record) => (
          <Space size={0} className='misconfigured-tags'>
            <Tag color="blue">
              {intl.formatMessage({ id: 'dtcMappingValidation.module' })}:{' '}
              {record.is_supported ?? '-'}
            </Tag>
            <Tag color={record.is_misconfigured ? 'orange' : 'default'}>
              {intl.formatMessage({ id: 'dtcMappingValidation.misconfig' })}:{' '}
              {record.is_misconfigured == null
                ? intl.formatMessage({ id: 'generic.no' })
                : intl.formatMessage({ id: 'generic.yes' })}
            </Tag>
            <Tag color="red">
              {intl.formatMessage({ id: 'dtcMappingValidation.noResults' })}:{' '}
              {record.is_missing_component_in_graph ?? '-'}
            </Tag>
          </Space>
        ),
      },
    ],
    [
      intl,
      isSupported,
      isMissConfigured,
      isMissingComponentInGraph,
      onChangeIsSupported,
      onChangeIsMissConfigured,
      onChangeIsMissingComponentInGraph,
    ]
  )
  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%', verticalAlign: 'top' }}
    >
      <Table
        rowKey={(record) => `dtc-mapping-validation-${record.id}`}
        columns={columns}
        dataSource={dataSource}
        showSorterTooltip={false}
        scroll={dataSource?.length ? { x: 750 } : undefined}
        pagination={
          pagination
            ? {
                current: pagination?.currentPage,
                total: pagination?.totalCount,
                pageSize: 10,
                showSizeChanger: false,
                hideOnSinglePage: true,
                showTotal: (total) => <RecordsCount count={total} />,
                onChange: (page) => {
                  const offset = (page - 1) * 10
                  const url = pagination?.nextPageLink
                    ? new URL(pagination?.nextPageLink)
                    : new URL(pagination?.prevPageLink)
                  url.searchParams.set('offset', offset)
                  getDtcMappingValidationList?.(url.toString())
                },
              }
            : false
        }
        locale={{
          emptyText: (
            <div style={{ padding: '40px' }}>
              <Empty description={<RecordsCount count={0} />} />
            </div>
          ),
        }}
      />
      {pagination && pagination?.totalPages <= 1 && pagination?.totalCount > 0 && (
        <Typography.Text>
          <RecordsCount count={pagination?.totalCount} />
        </Typography.Text>
      )}
    </Space>
  )
}

export default DtcMappingValidationTable
